import React from 'react';
import FeedResultCard from '../../components/FeedResultCard';
import { FEED_CHIP } from '../../components/investigation/feedCardStyles';
import { sanitizeHtmlToText } from '../../utils/sanitizeHtml';

const ALERT_SHELL =
  'border-red-500/40 bg-red-950/30 hover:border-red-400/60 shadow-[0_0_10px_rgba(239,68,68,0.08)]';
const CLEAN_SHELL = 'border-emerald-500/30 hover:border-emerald-400/40';

/**
 * Corporate HIBP domain breach card (dark glass feed layout).
 *
 * @param {{ res: object, reactKey?: string | number }} props
 */
export default function CorporateBreachesCard({ res, reactKey }) {
  const breachedCount = Number(res?.breached_platforms_count || 0);
  const domain = String(res?.domain || '').trim();
  const hibpDomainUrl = domain
    ? `https://haveibeenpwned.com/PwnedWebsites#${encodeURIComponent(domain)}`
    : 'https://haveibeenpwned.com/PwnedWebsites';
  const primaryBreachName = res?.details?.[0]?.name || res?.details?.[0]?.title || '';
  const hibpBreachUrl = primaryBreachName
    ? `https://haveibeenpwned.com/Breach/${encodeURIComponent(primaryBreachName)}`
    : hibpDomainUrl;
  const isClean = breachedCount === 0;

  const inspectRow = {
    ui_bucket: 'corporate_breaches',
    platform: 'HIBP',
    title: isClean
      ? 'Corporate Data Leaks — Clean'
      : `Corporate Data Leaks — ${primaryBreachName || `${breachedCount} databases`}`,
    snippet: isClean
      ? 'No leaks were found in public breach databases.'
      : `Exposed in ${breachedCount} database(s) for ${domain || 'corporate domain'}.`,
    url: isClean ? hibpDomainUrl : hibpBreachUrl,
    details: res?.details,
    domain,
  };

  const breachList =
    !isClean && Array.isArray(res?.details) && res.details.length > 0 ? (
      <div className="grid grid-cols-1 gap-3">
        {res.details.map((breach, bIdx) => (
          <div
            key={`${breach?.title || breach?.name || 'breach'}-${bIdx}`}
            className="rounded-xl border border-red-500/20 bg-slate-950/60 p-4"
          >
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-bold uppercase text-slate-100">
                {breach.title || breach.name}
              </span>
              {breach.breach_date ? (
                <span className={`${FEED_CHIP} border-red-500/25 text-red-200`}>
                  {breach.breach_date}
                </span>
              ) : null}
            </div>
            {breach.pwn_count != null ? (
              <p className="text-xs text-slate-400">
                <span className="font-black text-red-300">{Number(breach.pwn_count).toLocaleString()}</span>
                {' '}
                records compromised.
              </p>
            ) : null}
            {breach.description ? (
              <p className="mt-3 rounded-lg border border-white/5 bg-slate-900/50 p-2.5 text-[11px] leading-relaxed text-slate-300">
                {sanitizeHtmlToText(breach.description)}
              </p>
            ) : null}
            {Array.isArray(breach.data_classes) && breach.data_classes.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-1">
                {breach.data_classes.map((dc) => (
                  <span key={dc} className={`${FEED_CHIP} border-red-500/20 text-red-200`}>
                    {dc}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    ) : null;

  return (
    <FeedResultCard
      key={reactKey}
      res={inspectRow}
      title={
        isClean
          ? 'Corporate Data Leaks — No leaks found'
          : `Corporate Data Leaks — ${breachedCount} database(s)`
      }
      icon={isClean ? '✅' : '🔓'}
      isAlert={!isClean}
      shellClassName={isClean ? CLEAN_SHELL : ALERT_SHELL}
      footer={breachList}
    />
  );
}
